/**
 * Regras do painel "Amigos e chat" (docs/spec/friends.md), sem React nem store: os grupos da lista,
 * a linha de situação de cada amigo, o "visto há", o agrupamento das mensagens e as janelas de
 * conversa abertas.
 */
import type { ContactList, ContactView, ConversationView, FriendPresence, ImsMessage } from '../shared.js';

/** A cor do ponto na foto. O meu status (`UserStatus`) também serve: são três destes. */
export type PresenceDot = 'online' | 'away' | 'busy' | 'offline';

export function presenceDot(presence: FriendPresence): PresenceDot {
  if (!presence.online) return 'offline';
  if (presence.busy) return 'busy';
  return presence.away ? 'away' : 'online';
}

/** O grupo da lista: NA MESA, ONLINE (inclui ausente) ou OFFLINE. Os escondidos têm o grupo deles. */
export type FriendGroupKey = 'room' | 'online' | 'offline' | 'hidden';

export function presenceGroup(presence: FriendPresence): Exclude<FriendGroupKey, 'hidden'> {
  if (!presence.online) return 'offline';
  return presence.inRoom ? 'room' : 'online';
}

/** Para buscar sem diferenciar maiúsculas nem acentos. */
export function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

export type FriendGroup = { key: FriendGroupKey; friends: ContactView[] };

const GROUP_ORDER: FriendGroupKey[] = ['room', 'online', 'offline', 'hidden'];

/** Os grupos com alguém dentro, na ordem da tela, cada um em ordem alfabética; `query` filtra pelo nome. */
export function groupFriends(contacts: Pick<ContactList, 'friends' | 'hidden'>, query = ''): FriendGroup[] {
  const q = fold(query.trim());
  const match = (c: ContactView) => !q || fold(c.user.displayName).includes(q);
  const byName = (a: ContactView, b: ContactView) => a.user.displayName.localeCompare(b.user.displayName);
  const groups = new Map<FriendGroupKey, ContactView[]>(GROUP_ORDER.map((key) => [key, []]));
  for (const contact of contacts.friends) if (match(contact)) groups.get(presenceGroup(contact.presence))!.push(contact);
  for (const contact of contacts.hidden) if (match(contact)) groups.get('hidden')!.push(contact);
  return GROUP_ORDER.map((key) => ({ key, friends: groups.get(key)!.sort(byName) })).filter((group) => group.friends.length > 0);
}

/** Uma frase traduzida: a chave do i18n e os valores. */
export type Phrase = { key: string; params?: Record<string, string | number> };

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "visto agora há pouco", "há 5 min", "há 2 h", "ontem", "há 3 dias". `prefix` troca o começo das
 * chaves para outra frase com os mesmos degraus (a última visita a uma mesa, no Início).
 */
export function seenAgo(lastSeenAt: number | null, now: number, prefix = 'friends.seen'): Phrase {
  if (lastSeenAt === null) return { key: `${prefix}.unknown` };
  const ago = Math.max(0, now - lastSeenAt);
  if (ago < MINUTE) return { key: `${prefix}.now` };
  if (ago < HOUR) return { key: `${prefix}.minutes`, params: { n: Math.floor(ago / MINUTE) } };
  if (ago < DAY) return { key: `${prefix}.hours`, params: { n: Math.floor(ago / HOUR) } };
  if (ago < 2 * DAY) return { key: `${prefix}.yesterday` };
  return { key: `${prefix}.days`, params: { n: Math.floor(ago / DAY) } };
}

/** O tom da linha de situação (a cor dela). */
export type StatusTone = 'typing' | 'room' | 'online' | 'away' | 'busy' | 'offline';

/**
 * A linha embaixo do nome: "digitando…" vence; depois a mesa (com "Ocupado ·" ou "Ausente ·" na
 * frente), ocupado, ausente, online ou o "visto há".
 */
export function statusLine(contact: Pick<ContactView, 'presence' | 'lastSeenAt'>, typing: boolean, now: number): Phrase & { tone: StatusTone; seen?: Phrase } {
  const { presence } = contact;
  if (typing && presence.online) return { key: 'friends.status.typing', tone: 'typing' };
  if (!presence.online) return { key: 'friends.status.offline', tone: 'offline', seen: seenAgo(contact.lastSeenAt, now) };
  if (presence.inRoom) {
    const where: Phrase & { tone: StatusTone } = presence.room
      ? { key: 'friends.status.room', params: { room: presence.room.name }, tone: 'room' }
      : { key: 'friends.status.someRoom', tone: 'room' };
    if (presence.busy) return { ...where, key: `${where.key}.busy` };
    return presence.away ? { ...where, key: `${where.key}.away` } : where;
  }
  if (presence.busy) return { key: 'friends.status.busy', tone: 'busy' };
  return presence.away ? { key: 'friends.status.away', tone: 'away' } : { key: 'friends.status.online', tone: 'online' };
}

/** Quantos amigos estão online, ocupados e ausentes também (o resumo do cabeçalho). */
export function onlineCount(friends: readonly ContactView[]): number {
  return friends.filter((friend) => friend.presence.online).length;
}

/** Mensagens não lidas de todas as conversas, amigos e "Outras conversas" (o número do botão e do cabeçalho). */
export function unreadTotal(contacts: (Pick<ContactList, 'friends' | 'hidden'> & Partial<Pick<ContactList, 'others'>>) | null): number {
  if (!contacts) return 0;
  let total = 0;
  for (const contact of [...contacts.friends, ...contacts.hidden, ...(contacts.others ?? [])]) total += contact.unreadCount;
  return total;
}

/**
 * As "Outras conversas" (quem não é amigo) que casam com a busca, da mais recente para a mais
 * antiga.
 */
export function otherConversations(others: readonly ConversationView[], query: string): ConversationView[] {
  const q = fold(query.trim());
  return others
    .filter((c) => !q || fold(c.user.displayName).includes(q))
    .sort((a, b) => (b.lastMessage?.at ?? 0) - (a.lastMessage?.at ?? 0));
}

/** Mensagens seguidas da mesma pessoa, com até 5 min entre elas, formam um grupo. */
export const GROUP_GAP_MS = 5 * MINUTE;

export type ChatItem =
  | { kind: 'day'; at: number; day: 'today' | 'yesterday' | 'date' }
  | { kind: 'message'; message: ImsMessage; mine: boolean; first: boolean; last: boolean };

function startOfDay(at: number): number {
  const date = new Date(at);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/** As mensagens com o separador de cada dia e as pontas dos grupos (a hora vai só na última). */
export function chatItems(messages: readonly ImsMessage[], meId: string | undefined, now: number): ChatItem[] {
  const items: ChatItem[] = [];
  const today = startOfDay(now);
  let lastDay = -1;
  messages.forEach((message, i) => {
    const day = startOfDay(message.createdAt);
    if (day !== lastDay) {
      lastDay = day;
      items.push({ kind: 'day', at: message.createdAt, day: day === today ? 'today' : day === today - DAY ? 'yesterday' : 'date' });
    }
    const prev = messages[i - 1];
    const next = messages[i + 1];
    const together = (a: ImsMessage | undefined, b: ImsMessage | undefined) =>
      !!a && !!b && a.fromId === b.fromId && !a.invite && !b.invite && Math.abs(b.createdAt - a.createdAt) <= GROUP_GAP_MS && startOfDay(a.createdAt) === startOfDay(b.createdAt);
    items.push({ kind: 'message', message, mine: message.fromId === meId, first: !together(prev, message), last: !together(message, next) });
  });
  return items;
}

/** Uma janela de conversa aberta embaixo. */
export type ChatWindow = { userId: string; minimized: boolean };

/** Janelas que cabem ao lado da coluna de amigos (até 3); no celular, uma. */
export function maxWindows(viewportWidth: number, narrow = false): number {
  if (narrow) return 1;
  // trilho (64) + coluna de amigos (320) + margens; cada janela 300 + 12 de vão
  const room = viewportWidth - 64 - 320 - 32;
  return Math.max(1, Math.min(3, Math.floor(room / 312)));
}

/**
 * Abre a conversa (ou expande a que já está aberta). Passando do limite, sai a mais antiga.
 * `minimized`: abre minimizada (mensagem que chegou) e não mexe numa janela que já está aberta.
 */
export function withWindow(windows: readonly ChatWindow[], userId: string, max: number, minimized = false): ChatWindow[] {
  const found = windows.find((w) => w.userId === userId);
  if (found) return minimized ? [...windows] : windows.map((w) => (w.userId === userId ? { ...w, minimized: false } : w));
  const next = [...windows, { userId, minimized }];
  return next.length > max ? next.slice(next.length - max) : next;
}

/** Dá para abrir mais uma janela sem fechar nenhuma? (mensagem que chega nunca tira uma janela aberta) */
export function hasRoomForWindow(windows: readonly ChatWindow[], max: number): boolean {
  return windows.length < max;
}

/** O relógio do "Ausente" (social/idle.ts), sem DOM. */
export function createIdleClock(afterMs: number, now = Date.now()) {
  let last = now;
  let away = false;
  return {
    /** houve uso; devolve true se isso tirou do "ausente" */
    activity(at: number): boolean {
      last = at;
      if (!away) return false;
      away = false;
      return true;
    },
    /** o tempo passou; devolve true se agora ficou "ausente" */
    tick(at: number): boolean {
      if (away || at - last < afterMs) return false;
      away = true;
      return true;
    },
    get away(): boolean {
      return away;
    },
  };
}
